/*
  GA4 client/session id + first-touch attribution → Shopify cart attributes (#144 / GA4-E2-03).

  WHY THIS EXISTS
  The server-side purchase event (#145) fires from a backend that has no access to the browser's
  `_ga` cookie. Without the original client_id, every order it recovers lands in GA4 as a brand-new
  Direct session — we would fix the 41.7% count (#142) and destroy channel reporting in the same
  release. This carries the browser's identifiers through the cart into the order, where the
  orders/create webhook can read them back out of `note_attributes`.

  WHAT IT WRITES (all underscore-prefixed, so Shopify hides them from the customer-facing order
  summary — they are plumbing, not something a shopper should see on their confirmation):

    _ga_client_id      GA4 client id, from the `_ga` cookie
    _ga_session_id     GA4 session id, from the `_ga_<measurement>` cookie
    _ga_id_source      "cookie" | "fallback"  — see the no-cookie case below
    _attr_source       first-touch utm_source
    _attr_medium       first-touch utm_medium
    _attr_campaign     first-touch utm_campaign
    _attr_gclid        first-touch gclid
    _attr_landing      first-touch landing path
    _attr_ts           first-touch timestamp (ISO)

  FIRST-TOUCH, NOT LAST-TOUCH. The GA4 session can expire while a cart sits for a day; #145 needs to
  attribute the order even then. First touch is stored once in localStorage and never overwritten,
  so a shopper who arrives from a campaign, leaves, and returns direct is still credited to the
  campaign rather than to `(direct)`.

  NO PII. Only identifiers and campaign values — never email, phone, name or address. Cart
  attributes travel to the order and into any downstream export, so this is a hard line
  (CLAUDE.md rule 5, and the tracking plan's "do not do this").

  NOT dependent on GTM. It reads the `_ga` cookie, which is domain-level and written by whichever
  GA4 instance is running — today the Google & YouTube app pixel, tomorrow the GTM config tag
  (#138). Both write the same client id, so this works before and after that migration and needs no
  change when it happens.
*/
(function () {
  "use strict";

  var MEASUREMENT_ID = "G-MHKC4DNNH3";
  var SESSION_COOKIE = "_ga_" + MEASUREMENT_ID.replace(/^G-/, "");
  var FIRST_TOUCH_KEY = "om_first_touch";
  var SYNCED_KEY = "om_attr_synced";

  function cookie(name) {
    var m = document.cookie.match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)");
    return m ? m.pop() : "";
  }

  /* `_ga` is `GA1.1.<client_id>` — the client id is everything after the second dot, and it
     contains a dot itself (`1234567890.1699999999`), so split on the first two only. */
  function clientId() {
    var raw = cookie("_ga");
    if (!raw) return "";
    var parts = raw.split(".");
    return parts.length >= 4 ? parts.slice(2).join(".") : "";
  }

  /* `_ga_<id>` has had two shapes, and GS2 changed the STRUCTURE, not just the prefix:

       GS1   GS1.1.<session_id>.<n>.<...>          dot-delimited, session id is field 3
       GS2   GS2.1.s<session_id>$o1$g1$t<...>$h0   field 3 is a `$`-delimited blob, session id
                                                   is its first part, `s`-prefixed

     So field 3 has to be unpacked rather than used as-is — reading it positionally on a live GS2
     cookie returns the whole `$` blob and writes a malformed session id into the cart, which then
     joins to nothing in GA4. Handles both forms: GS1's field 3 has no `$` and no `s` prefix. */
  function sessionId() {
    var raw = cookie(SESSION_COOKIE);
    if (!raw) return "";
    var parts = raw.split(".");
    if (parts.length < 3) return "";
    var field = parts[2].split("$")[0];
    return field.charAt(0) === "s" ? field.slice(1) : field;
  }

  function store(key, value) {
    try { localStorage.setItem(key, value); } catch (e) { /* private mode — degrade, don't throw */ }
  }
  function read(key) {
    try { return localStorage.getItem(key) || ""; } catch (e) { return ""; }
  }
  function clear(key) {
    try { localStorage.removeItem(key); } catch (e) { /* private mode — degrade, don't throw */ }
  }

  /* Consent declined, cookies blocked, or GA4 simply hasn't written yet. Dropping the order from
     the server-side event entirely would be worse than attributing it imperfectly, so mint a
     stable pseudo id in the GA4 client-id shape and FLAG it, so these can be counted separately
     and excluded from any analysis that needs a real GA4 join (#144 acceptance criterion 6). */
  function fallbackClientId() {
    var existing = read("om_pseudo_cid");
    if (existing) return existing;
    var rand = Math.floor(Math.random() * 1e10);
    var secs = Math.floor(Date.now() / 1000);
    var id = rand + "." + secs;
    store("om_pseudo_cid", id);
    return id;
  }

  var touchCache = null;
  function firstTouch() {
    if (touchCache) return touchCache;
    var saved = read(FIRST_TOUCH_KEY);
    if (saved) {
      try { touchCache = JSON.parse(saved); return touchCache; } catch (e) { /* corrupted — re-capture */ }
    }

    var q = new URLSearchParams(window.location.search);
    var touch = {
      source: q.get("utm_source") || "",
      medium: q.get("utm_medium") || "",
      campaign: q.get("utm_campaign") || "",
      gclid: q.get("gclid") || "",
      landing: window.location.pathname,
      ts: new Date().toISOString(),
    };

    /* Only persist a touch that actually carries attribution, or the first page of a direct visit
       would permanently occupy the first-touch slot and lock out the campaign click that follows. */
    if (touch.source || touch.medium || touch.campaign || touch.gclid) {
      store(FIRST_TOUCH_KEY, JSON.stringify(touch));
    }
    touchCache = touch;
    return touch;
  }

  function attributes() {
    var cid = clientId();
    var touch = firstTouch();

    return {
      _ga_client_id: cid || fallbackClientId(),
      _ga_session_id: sessionId(),
      _ga_id_source: cid ? "cookie" : "fallback",
      _attr_source: touch.source,
      _attr_medium: touch.medium,
      _attr_campaign: touch.campaign,
      _attr_gclid: touch.gclid,
      _attr_landing: touch.landing,
      _attr_ts: touch.ts,
    };
  }

  /* A signature of what we would write, so a cart that already carries these values doesn't get a
     redundant POST on every page view. The session id changes when GA4 rolls the session, which is
     exactly when we DO want to re-sync. */
  function signature(attrs) {
    return attrs._ga_client_id + "|" + attrs._ga_session_id;
  }

  function sync(force) {
    var attrs = attributes();
    var sig = signature(attrs);
    if (!force && read(SYNCED_KEY) === sig) return Promise.resolve();

    return fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ attributes: attrs }),
    })
      .then(function (res) {
        if (res.ok) store(SYNCED_KEY, sig);
      })
      .catch(function () {
        /* Attribution is best-effort. A failed cart update must never surface to the shopper or
           block an add-to-cart — the order still completes, it just lands less well attributed. */
      });
  }

  /* Capture first touch immediately, even if nothing is ever added to the cart — the campaign
     parameters are on the URL now and will be gone by the time a cart exists. */
  firstTouch();

  /* Write to the cart when there is a cart to write to. Shopify's cart endpoints are shared by
     every add-to-cart path in this theme (there are several), so hook the response rather than
     each call site: any successful /cart/add.js means a cart now exists. */
  /* `origFetch` is applied to `window`, never to `this`. A detached call (`var f = window.fetch;
     f(url)`) or a bare `fetch` from bundled/strict code arrives with `this` undefined, and
     `fetch.apply(undefined, …)` throws `Illegal invocation` — which would break that add-to-cart or
     API path site-wide, for an attribution feature that is supposed to be best-effort. */
  var origFetch = window.fetch;
  if (typeof origFetch === "function") {
    window.fetch = function (input, init) {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      var isAdd = url.indexOf("/cart/add") !== -1;
      return origFetch.apply(window, arguments).then(function (res) {
        if (isAdd && res.ok) sync(false);
        return res;
      });
    };
  }

  /* A cart resumed in a later session: the cart survives, the GA4 session id does not, so refresh
     on load whenever the cart is non-empty (#144 step 3). */
  document.addEventListener("DOMContentLoaded", function () {
    /* No `cart` cookie means Shopify has never opened a cart for this browser, so there is nothing
       to read and nothing to attribute — skip the request rather than firing it on every page load
       of every session, including the ones that never shop. */
    if (!cookie("cart")) return;

    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        if (cart.item_count > 0) {
          sync(false);
          return;
        }
        /* An empty cart is the observable end of a completed order. SYNCED_KEY is keyed on
           client|session only, so without this a SECOND order inside the same 30-minute GA4 session
           matches the stored signature, `sync()` short-circuits, and the fresh cart carries no
           attributes at all — #145's webhook then reads empty `note_attributes` and the order lands
           as (direct), which is the precise failure this file exists to prevent. */
        clear(SYNCED_KEY);
      })
      .catch(function () { /* no cart, or offline — nothing to attribute yet */ });
  });
})();
